// components/hr/RehireModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemContent,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  Sheet,
  Stack,
} from '@mui/joy';
import {
  MagnifyingGlass as SearchOutlined,
  ArrowCounterClockwise as RehireOutlined,
  CheckCircle as CheckCircleOutlined,
  Warning as WarningOutlined,
} from '@phosphor-icons/react';
import { apiClient } from '../../lib/api';

/** One step the returning worker still has to complete, and why. */
interface RehireStep {
  step_id: string;
  title: string;
  type: string;
  reason: string;
}

/** A period a person worked here. */
interface EmploymentStint {
  id: string;
  started_on: string | null;
  ended_on: string | null;
  end_reason: string;
  end_reason_display: string;
  member_type: string;
  job_title: string;
  is_current: boolean;
}

/** A former team member, from GET /rehire-candidates/. */
interface RehireCandidate {
  id: string;
  full_name: string;
  email: string;
  member_type: string;
  status: string;
  job_title: string;
  department: string;
  termination_date: string | null;
  last_stint: EmploymentStint | null;
  seasons_worked: number;
}

/** What rehiring one person would do — the same plan the rehire itself runs on. */
interface RehirePlan {
  team_member_id: string;
  full_name: string;
  email: string;
  job_title: string;
  seasons_worked: number;
  package: { id: string; name: string; is_rehire_package: boolean } | null;
  requires: RehireStep[];
  carried_over: RehireStep[];
  can_rehire: boolean;
  blocker: string;
}

interface RehireResult {
  team_member_id: string;
  full_name: string;
  status: 'rehired' | 'skipped';
  reason: string;
  invitation_sent?: boolean;
}

interface RehireModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-ticked when the modal is opened from a single person's row menu. */
  initialMemberIds?: string[];
  /** Called with how many people actually came back, never for a no-op run. */
  onSuccess?: (rehiredCount: number) => void;
}

/** Everyone who has left and can be brought back, most recent departure first. */
const useRehireCandidates = (enabled: boolean) =>
  useQuery({
    queryKey: ['team-members', 'rehire-candidates'],
    queryFn: async () => {
      const response = await apiClient.get<{ count: number; results: RehireCandidate[] }>(
        '/api/hr/team-members/rehire-candidates/'
      );
      return response.data.results;
    },
    enabled,
  });

/**
 * What each selected person would be asked for. Read-only — nothing is rehired
 * until the button is pressed.
 */
const useRehirePreview = (memberIds: string[], enabled: boolean) =>
  useQuery({
    queryKey: ['team-members', 'rehire-preview', [...memberIds].sort()],
    queryFn: async () => {
      const response = await apiClient.post<{ plans: RehirePlan[]; rehirable_count: number }>(
        '/api/hr/team-members/rehire-preview/',
        { member_ids: memberIds }
      );
      return response.data;
    },
    enabled: enabled && memberIds.length > 0,
  });

/** Idempotent on the backend: anyone already back on the roster is skipped. */
const useBatchRehire = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      member_ids: string[];
      start_date: string;
      send_invitation: boolean;
    }) => {
      const response = await apiClient.post<{
        results: RehireResult[];
        rehired_count: number;
        skipped_count: number;
      }>('/api/hr/team-members/batch-rehire/', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions'] });
    },
  });
};

const formatDate = (value: string | null) =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString() : '—';

/** "Mar 3, 2025 – Nov 14, 2025 · Seasonal Layoff" */
const describeStint = (candidate: RehireCandidate) => {
  const stint = candidate.last_stint;
  if (!stint) {
    return candidate.termination_date
      ? `Left ${formatDate(candidate.termination_date)}`
      : 'No recorded season';
  }
  const reason = stint.end_reason_display ? ` · ${stint.end_reason_display}` : '';
  return `${formatDate(stint.started_on)} – ${formatDate(stint.ended_on)}${reason}`;
};

export const RehireModal: React.FC<RehireModalProps> = ({
  open,
  onClose,
  initialMemberIds,
  onSuccess,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sendInvitations, setSendInvitations] = useState(true);
  const [results, setResults] = useState<RehireResult[] | null>(null);
  const [error, setError] = useState('');

  const { data: candidates = [], isLoading } = useRehireCandidates(open);
  const preview = useRehirePreview(selectedIds, open && !results);
  const batchRehire = useBatchRehire();

  // Reopening starts a fresh run; the previous run's results are stale.
  useEffect(() => {
    if (!open) return;
    setSelectedIds(initialMemberIds ?? []);
    setSearch('');
    setResults(null);
    setError('');
    setSendInvitations(true);
    setStartDate(new Date().toISOString().slice(0, 10));
  }, [open, initialMemberIds]);

  const visibleCandidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return candidates;
    return candidates.filter((candidate) =>
      `${candidate.full_name} ${candidate.email} ${candidate.job_title}`
        .toLowerCase()
        .includes(term)
    );
  }, [candidates, search]);

  const allVisibleSelected =
    visibleCandidates.length > 0 &&
    visibleCandidates.every((candidate) => selectedIds.includes(candidate.id));

  const toggleMember = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
    );
  };

  const toggleAllVisible = () => {
    const visibleIds = visibleCandidates.map((candidate) => candidate.id);
    setSelectedIds((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...current, ...visibleIds]))
    );
  };

  const handleRehire = async () => {
    setError('');
    try {
      const response = await batchRehire.mutateAsync({
        member_ids: selectedIds,
        start_date: startDate,
        send_invitation: sendInvitations,
      });
      setResults(response.results);
      if (response.rehired_count > 0) {
        onSuccess?.(response.rehired_count);
      }
    } catch (err: unknown) {
      const detail = (err as { responseData?: { error?: string } })?.responseData?.error;
      setError(detail || 'Failed to rehire the selected team members');
    }
  };

  const plansById = useMemo(() => {
    const map = new Map<string, RehirePlan>();
    (preview.data?.plans ?? []).forEach((plan) => map.set(plan.team_member_id, plan));
    return map;
  }, [preview.data]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: { xs: 'auto', sm: 720 }, maxHeight: '90vh', overflowY: 'auto' }}>
        <ModalClose />
        <DialogTitle>Rehire Crew</DialogTitle>
        <Typography level="body-sm" color="neutral">
          Bring last season's crew back in one action. Everyone keeps their record — they are
          only asked for what expired or changed while they were away.
        </Typography>

        <DialogContent sx={{ gap: 2 }}>
          {error && <Alert color="danger">{error}</Alert>}

          {results ? (
            <Stack spacing={1.5}>
              <Alert
                color={results.some((r) => r.status === 'rehired') ? 'success' : 'neutral'}
                startDecorator={<CheckCircleOutlined />}
              >
                {`${results.filter((r) => r.status === 'rehired').length} rehired, ` +
                  `${results.filter((r) => r.status === 'skipped').length} skipped`}
              </Alert>
              <List size="sm">
                {results.map((result) => (
                  <ListItem key={result.team_member_id}>
                    <ListItemContent>
                      <Typography level="title-sm">{result.full_name}</Typography>
                      <Typography level="body-xs" color="neutral">
                        {result.status === 'rehired'
                          ? result.invitation_sent
                            ? 'Rehired — onboarding invitation sent'
                            : 'Rehired — no invitation sent'
                          : result.reason}
                      </Typography>
                    </ListItemContent>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={result.status === 'rehired' ? 'success' : 'neutral'}
                    >
                      {result.status === 'rehired' ? 'Rehired' : 'Skipped'}
                    </Chip>
                  </ListItem>
                ))}
              </List>
            </Stack>
          ) : (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ alignItems: { sm: 'flex-end' } }}
              >
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Find former team members</FormLabel>
                  <Input
                    placeholder="Search by name, email or job title..."
                    startDecorator={<SearchOutlined />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </FormControl>
                <FormControl sx={{ flex: { sm: '0 0 180px' } }}>
                  <FormLabel>Start date</FormLabel>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </FormControl>
              </Stack>

              {isLoading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CircularProgress size="sm" />
                </Box>
              ) : candidates.length === 0 ? (
                <Alert color="neutral">
                  Nobody has left yet. Terminated and inactive team members show up here ready to
                  be brought back.
                </Alert>
              ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', maxHeight: 260, overflowY: 'auto' }}>
                  <List size="sm">
                    <ListItem>
                      <Checkbox
                        size="sm"
                        checked={allVisibleSelected}
                        indeterminate={!allVisibleSelected && selectedIds.length > 0}
                        onChange={toggleAllVisible}
                        label={
                          <Typography level="title-sm">
                            {allVisibleSelected ? 'Clear selection' : 'Select everyone shown'}
                          </Typography>
                        }
                      />
                    </ListItem>
                    <Divider />
                    {visibleCandidates.map((candidate) => (
                      <ListItem key={candidate.id}>
                        <Checkbox
                          size="sm"
                          checked={selectedIds.includes(candidate.id)}
                          onChange={() => toggleMember(candidate.id)}
                          sx={{ mr: 1 }}
                        />
                        <ListItemContent>
                          <Typography level="title-sm">
                            {candidate.full_name}
                            {candidate.job_title ? ` · ${candidate.job_title}` : ''}
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            {describeStint(candidate)}
                          </Typography>
                        </ListItemContent>
                        <Chip size="sm" variant="soft">
                          {candidate.seasons_worked === 1
                            ? '1 season'
                            : `${candidate.seasons_worked} seasons`}
                        </Chip>
                      </ListItem>
                    ))}
                  </List>
                </Sheet>
              )}

              {selectedIds.length > 0 && (
                <Box>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    What they will be asked for
                  </Typography>
                  {preview.isLoading ? (
                    <CircularProgress size="sm" />
                  ) : (
                    <Stack spacing={1}>
                      {selectedIds.map((id) => {
                        const plan = plansById.get(id);
                        if (!plan) return null;
                        return (
                          <Sheet
                            key={id}
                            variant="soft"
                            sx={{ borderRadius: 'sm', p: 1.5 }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography level="title-sm">{plan.full_name}</Typography>
                              {plan.package && (
                                <Typography level="body-xs" color="neutral">
                                  {plan.package.name}
                                </Typography>
                              )}
                            </Box>
                            {plan.can_rehire ? (
                              <>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {plan.requires.length === 0 ? (
                                    <Chip size="sm" color="success" variant="soft">
                                      Nothing outstanding
                                    </Chip>
                                  ) : (
                                    plan.requires.map((step) => (
                                      <Chip
                                        key={step.step_id}
                                        size="sm"
                                        color="warning"
                                        variant="soft"
                                        title={step.reason}
                                      >
                                        {step.title}
                                      </Chip>
                                    ))
                                  )}
                                </Box>
                                {plan.requires.map((step) => (
                                  <Typography key={step.step_id} level="body-xs" color="neutral">
                                    {step.title}: {step.reason}
                                  </Typography>
                                ))}
                                {plan.carried_over.length > 0 && (
                                  <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                    Carried over from last season:{' '}
                                    {plan.carried_over.map((step) => step.title).join(', ')}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Typography
                                level="body-xs"
                                color="danger"
                                startDecorator={<WarningOutlined />}
                              >
                                {plan.blocker}
                              </Typography>
                            )}
                          </Sheet>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              )}

              <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                <Box>
                  <FormLabel>Send onboarding invitations</FormLabel>
                  <Typography level="body-xs" color="neutral">
                    Emails everyone their link now. Turn off to send it later.
                  </Typography>
                </Box>
                <Switch
                  checked={sendInvitations}
                  onChange={(e) => setSendInvitations(e.target.checked)}
                />
              </FormControl>
            </>
          )}
        </DialogContent>

        <DialogActions>
          {results ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <>
              <Button variant="plain" color="neutral" onClick={onClose}>
                Cancel
              </Button>
              <Button
                startDecorator={<RehireOutlined />}
                loading={batchRehire.isPending}
                disabled={selectedIds.length === 0}
                onClick={handleRehire}
              >
                {selectedIds.length > 1
                  ? `Rehire ${selectedIds.length} people`
                  : 'Rehire'}
              </Button>
            </>
          )}
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default RehireModal;
